import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardBody, Table } from "reactstrap";
import { Link } from "react-router-dom";
import api, { FILE_BASE_URL } from '../../utils/api';

const RecentAdditions = () => {
    const [recentAdditions, setRecentAdditions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [failedImages, setFailedImages] = useState(new Set());

    const formatDate = (dateString) => {
        if (!dateString) return 'غير محدد';
        
        const date = new Date(dateString);
        
        // تنسيق التاريخ: 19-8-2025
        const day = date.getDate();
        const month = date.getMonth() + 1; // +1 لأن getMonth() يبدأ من 0
        const year = date.getFullYear();
        
        // تنسيق الوقت: 12:52 pm
        const hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'pm' : 'am';
        const displayHours = hours % 12 || 12; // تحويل إلى نظام 12 ساعة
        
        return `${day}-${month}-${year} | ${displayHours}:${minutes} ${ampm}`;
    };

    // دالة لتحديد المسار حسب النوع
    const getRoutePrefix = (type) => {
        switch(type) {
            case 'صحفي': return '/dashboard/journalists';
            case 'ناشط': return '/dashboard/activists';
            case 'مؤسسة': return '/dashboard/institutions';
            default: return '#';
        }
    };
    // دالة لإنشاء الدائرة الملونة - متطابقة مع ألوان DonutChart
    const getAvatarCircle = (name, type) => {
        const firstLetter = name ? name.charAt(0).toUpperCase() : '؟';
        
        // نفس ألوان DonutChart المستخدمة في MonthlyEarnings
        const colors = {
            'مؤسسة': '#556ee6',    // نفس لون المؤسسات في DonutChart
            'صحفي': '#34c38f',     // نفس لون الصحفيين في DonutChart  
            'ناشط': '#f46a6a'      // نفس لون الناشطين في DonutChart
        };

        const bgColor = colors[type] || '#74788d'; // لون افتراضي

        return (
            <div 
                className="d-flex align-items-center justify-content-center rounded-circle"
                style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: bgColor,
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: 'bold'
                }}
            >
                {firstLetter}
            </div>
        );
    };

    // دالة للحصول على الأفاتار (صورة أو دائرة)
    const getProfileAvatar = (item, index) => {
        const imageKey = `${item.type}-${item.id}-${index}`;
        
        // إذا فشلت الصورة مسبقاً، اعرض الدائرة مباشرة
        if (!item.profile_image || failedImages.has(imageKey)) {
            return getAvatarCircle(item.full_name, item.type);
        }

        // تحديد مسار الصورة الصحيح
        let imageSrc = '';
        if (item.profile_image.startsWith('http')) {
            imageSrc = item.profile_image;
        } else {
            // المسار الصحيح حسب UploadFileController
imageSrc = `${FILE_BASE_URL}/uploads/images/${item.profile_image}`;
        }

        return (
            <img
                src={imageSrc}
                alt={item.full_name || 'صورة'}
                className="rounded-circle"
                style={{ 
                    width: '40px', 
                    height: '40px', 
                    objectFit: 'cover'
                }}
                onError={() => {
                    // أضف الصورة لقائمة الفاشلة
                    setFailedImages(prev => new Set([...prev, imageKey]));
                }}
            />
        );
    };

    const fetchRecentAdditions = async () => {
        try {
            setLoading(true);
            setError(null);

const [journalistsRes, activistsRes, institutionsRes] = await Promise.all([
               api.get("/journalists/latest-updated"),
               api.get("/activists/latest-updated"),
               api.get("/institutions/latest-updated")
           ]);
            // تحضير كل البيانات بدون تحديد العدد أولاً
            const journalists = (journalistsRes.data.data || journalistsRes.data || [])
                .map(item => ({
                    ...item,
                    type: 'صحفي',
                    typeClass: 'success'
                }));

            const activists = (activistsRes.data.data || activistsRes.data || [])
                .map(item => ({
                    ...item,
                    type: 'ناشط',
                    typeClass: 'danger'
                }));

            const institutions = (institutionsRes.data.data || institutionsRes.data || [])
                .map(item => ({
                    ...item,
                    full_name: item.title,
                    profile_image: item.logo,
                    type: 'مؤسسة',
                    typeClass: 'primary'
                }));

            // دمج كل البيانات
            const allAdditions = [...journalists, ...activists, ...institutions];
            
            // ترتيب حسب تاريخ التحديث وأخذ أول 4 فقط
            const sortedAdditions = allAdditions
                .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
                .slice(0, 4);

            setRecentAdditions(sortedAdditions);
            setLoading(false);

        } catch (error) {
            console.error("خطأ في جلب البيانات:", error);
            setError("فشل في تحميل البيانات");
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRecentAdditions();
    }, []);

    return (
        <Card style={{ minHeight: '455px' }}>
            <CardBody className="d-flex flex-column">
                <Row className="align-items-center mb-3">
                    <Col>
                        <h4 className="card-title mb-0">آخر التحديثات</h4>
                    </Col>
                    <Col className="col-auto">
                        <button 
                            className="btn btn-outline-primary btn-sm"
                            onClick={fetchRecentAdditions}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <div className="spinner-border spinner-border-sm me-1" role="status">
                                        <span className="sr-only">جاري التحميل...</span>
                                    </div>
                                    تحديث
                                </>
                            ) : (
                                <>
                                    <i className="mdi mdi-refresh me-1"></i>
                                    تحديث
                                </>
                            )}
                        </button>
                    </Col>
                </Row>

                <div className="flex-grow-1 d-flex flex-column justify-content-center">
                    {loading && recentAdditions.length === 0 ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">جاري التحميل...</span>
                            </div>
                            <p className="text-muted mt-2">جاري تحميل البيانات...</p>
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger" role="alert">
                            <i className="mdi mdi-alert-circle-outline me-2"></i>
                            {error}
                            <button 
                                className="btn btn-outline-danger btn-sm ms-2"
                                onClick={fetchRecentAdditions}
                            >
                                إعادة المحاولة
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive flex-grow-1">
                                <Table className="table table-borderless table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '60px' }}>الصورة</th>
                                            <th>الاسم</th>
                                            <th style={{ width: '100px' }}>النوع</th>
                                            <th style={{ width: '120px' }}>المحافظة</th>
                                            <th style={{ width: '120px' }}>المدينة</th>
                                            <th style={{ width: '100px' }}>الهاتف</th>
                                            <th style={{ width: '150px' }}>تاريخ التحديث</th>
                                            <th style={{ width: '120px' }}>الإجراءات</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recentAdditions.length === 0 ? (
                                            <tr>
                                                <td colSpan="8" className="text-center py-4 text-muted">
                                                    <i className="mdi mdi-information-outline me-2"></i>
                                                    لا توجد بيانات لعرضها
                                                </td>
                                            </tr>
                                        ) : (
                                            recentAdditions.map((item, index) => (
                                                <tr key={`${item.type}-${item.id}-${index}`}>
                                                    <td>
                                                        <div className="d-flex align-items-center justify-content-center">
                                                            {getProfileAvatar(item, index)}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <h6 className="mb-1">{item.full_name || 'غير محدد'}</h6>
                                                        {item.specialization && (
                                                            <p className="text-muted mb-0 small">
                                                                {item.specialization}
                                                            </p>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className={`badge bg-${item.typeClass}`}>
                                                            {item.type}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="text-muted small">
                                                            {item.country_province || 'غير محدد'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="text-muted small">
                                                            {item.city_or_area || 'غير محدد'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="text-muted small" dir="ltr">
                                                            {item.mobile1 || item.whatsapp1 || 'غير محدد'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="text-muted small" dir="ltr">
                                                            {formatDate(item.updated_at)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <Link
                                                                to={`${getRoutePrefix(item.type)}/view/${item.id}`}
                                                                className="btn btn-outline-info btn-sm"
                                                                title="عرض"
                                                            >
                                                                <i className="mdi mdi-eye"></i>
                                                            </Link>
                                                            <Link
                                                                to={`${getRoutePrefix(item.type)}/edit/${item.id}`}
                                                                className="btn btn-outline-warning btn-sm"
                                                                title="تحرير"
                                                            >
                                                                <i className="mdi mdi-pencil"></i>
                                                            </Link>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {recentAdditions.length > 0 && (
                                <div className="mt-auto pt-3 text-center">
                                    <small className="text-muted">
                                        عرض آخر 4 تحديثات من مجموع البيانات
                                    </small>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </CardBody>
        </Card>
    );
};

export default RecentAdditions;